/* Tusq · In-App Browser Banner  ·  v63.2 (2026-09-19)
 * ─────────────────────────────────────────────────
 * Detects FB / IG / TikTok / Pinterest / LinkedIn in-app webviews and offers
 * an "Open in Safari / Chrome" CTA for 1-tap Apple Pay / Google Pay checkout.
 *
 * v63.2 CORRECTION (Shishir feedback 2026-09-19):
 *   Tusq DOES NOT USE the /cart page — the entire flow is a slide-out cart
 *   drawer that never changes the URL. The v63.1 trigger (path=/cart) would
 *   have NEVER fired. This version:
 *     • Drops the /cart path check entirely.
 *     • Adds a MutationObserver that watches for the cart drawer becoming
 *       visible (Dawn <cart-drawer>.active, Zoorix .zrx-cart-drawer, body
 *       .overflow-hidden lock, anything matching [class*=cart-drawer]).
 *     • Keeps /checkouts/* and /orders/* URL paths as a defensive fallback.
 *     • Still listens for the custom events any theme code may fire.
 *
 * v63 changes retained:
 *   1. Copy is reason-first, device-aware:
 *        iOS      → "Open in Safari for 1-tap Apple Pay checkout"
 *        Android  → "Open in Chrome for 1-tap Google Pay checkout"
 *   2. URL is copied to the clipboard IMMEDIATELY on tap (not only as fallback).
 *   3. Rich dataLayer analytics (source, device, page, clipboard success).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tusq_inapp_banner_dismissed';
  var ua = (navigator.userAgent || navigator.vendor || window.opera || '').toString();

  // ── UA fingerprints ────────────────────────────────────────────────────────
  var isFacebook  = /FB_IAB|FBAN|FBAV|FB4A|FBIOS/i.test(ua);
  var isInstagram = /Instagram/i.test(ua);
  var isTikTok    = /BytedanceWebview|musical_ly|TikTok/i.test(ua);
  var isPinterest = /Pinterest/i.test(ua);
  var isLinkedIn  = /LinkedInApp/i.test(ua);
  var inAppWebview = isFacebook || isInstagram || isTikTok || isPinterest || isLinkedIn;

  if (!inAppWebview) return;

  // Honor per-session dismiss
  try {
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return;
  } catch (e) { /* private browsing — ignore */ }

  function isIOS()      { return /iPad|iPhone|iPod/.test(ua) && !window.MSStream; }
  function isAndroid()  { return /Android/i.test(ua); }

  // ── v63.2: high-intent trigger definitions ────────────────────────────────
  // (A) Fallback URL paths — for the rare user who lands on /checkouts/* or
  //     /orders/* directly (email link, receipt). /cart intentionally NOT here
  //     because Tusq uses a cart-drawer, not a /cart page.
  var HIGH_INTENT_PATHS = [
    /^\/checkouts?(\/|$|\?)/i,
    /^\/thank_you/i,
    /^\/orders?(\/|$|\?)/i
  ];
  function pathIsHighIntent() {
    var p = window.location.pathname || '/';
    for (var i = 0; i < HIGH_INTENT_PATHS.length; i++) {
      if (HIGH_INTENT_PATHS[i].test(p)) return true;
    }
    return false;
  }

  // (B) Drawer detection — the PRIMARY trigger for Tusq. We watch for any
  //     of these selectors becoming visible OR the body getting an
  //     overflow-lock class (which every drawer library does).
  var DRAWER_SELECTORS = [
    'cart-drawer',                 // Dawn custom element
    '.cart-drawer',                // Dawn class fallback
    '#CartDrawer',                 // Dawn id
    '.zrx-cart-drawer',            // Zoorix
    '[class*="cart-drawer"]',      // catch-all
    '[data-cart-drawer]'           // data-attribute pattern
  ];
  var BODY_LOCK_CLASSES = ['overflow-hidden', 'cart-drawer-open', 'no-scroll', 'is-scroll-locked'];

  function anyDrawerVisible() {
    // body lock class = drawer is open
    for (var i = 0; i < BODY_LOCK_CLASSES.length; i++) {
      if (document.body.classList.contains(BODY_LOCK_CLASSES[i])) return true;
    }
    // element visibility check
    for (var j = 0; j < DRAWER_SELECTORS.length; j++) {
      var els = document.querySelectorAll(DRAWER_SELECTORS[j]);
      for (var k = 0; k < els.length; k++) {
        var el = els[k];
        if (el.classList && (el.classList.contains('active') ||
                             el.classList.contains('is-open') ||
                             el.classList.contains('open') ||
                             el.classList.contains('visible'))) return true;
        if (el.hasAttribute && el.hasAttribute('open')) return true;
        // last resort: computed style visibility check
        try {
          var s = window.getComputedStyle(el);
          if (s.display !== 'none' && s.visibility !== 'hidden' &&
              parseFloat(s.opacity) > 0.05 && el.offsetWidth > 0 && el.offsetHeight > 0) {
            // Only count if not a bare shell — must actually be a fixed/absolute
            // overlay that would visually appear over the page
            if (s.position === 'fixed' || s.position === 'absolute') return true;
          }
        } catch (e) {}
      }
    }
    return false;
  }

  // ── v63.2: MutationObserver — fires banner as soon as drawer opens ────────
  var _observer = null;
  function startDrawerObserver() {
    if (_observer) return;
    _observer = new MutationObserver(function () {
      if (anyDrawerVisible()) showBanner();
    });
    // watch body + all likely drawer containers for class/attribute changes
    _observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    DRAWER_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        _observer.observe(el, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open'] });
      });
    });
    // Also watch for drawer nodes being ADDED to DOM later (some drawers inject on demand)
    _observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Copy helpers (unchanged from v63.1) ────────────────────────────────────
  function labelForDevice() {
    if (isIOS())     return { browser: 'Safari', wallet: 'Apple Pay' };
    if (isAndroid()) return { browser: 'Chrome', wallet: 'Google Pay' };
    return                    { browser: 'your default browser', wallet: '1-tap' };
  }

  function copyToClipboard(text) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {}
    try {
      var ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (e) { return false; }
  }

  function fireDataLayer(evt, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: evt };
      if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) payload[k] = extra[k];
      window.dataLayer.push(payload);
    } catch (e) {}
  }

  function sourceTag() {
    return isFacebook ? 'facebook' : isInstagram ? 'instagram' :
           isTikTok    ? 'tiktok'   : isPinterest ? 'pinterest' :
           isLinkedIn  ? 'linkedin' : 'other';
  }

  // ── Render / wire up ───────────────────────────────────────────────────────
  var _banner_wired = false;
  function showBanner() {
    var el = document.getElementById('tusq-inapp-banner');
    if (!el) return;
    if (_banner_wired) { el.hidden = false; return; }

    var lbl = labelForDevice();
    var strong = el.querySelector('.tusq-inapp-banner__text strong');
    var sub    = el.querySelector('.tusq-inapp-banner__text span');
    var cta    = el.querySelector('.tusq-inapp-banner__cta');
    if (strong) strong.textContent = 'Skip the typing.';
    if (sub)    sub.textContent    = 'Open in ' + lbl.browser + ' for 1-tap ' + lbl.wallet + ' checkout.';
    if (cta)    cta.textContent    = 'Open in ' + lbl.browser;

    el.hidden = false;

    fireDataLayer('tusq_inapp_banner_shown', {
      source: sourceTag(),
      trigger: pathIsHighIntent() ? 'path' : 'drawer',
      page: window.location.pathname
    });

    el.querySelector('[data-tusq-inapp-dismiss]').addEventListener('click', function () {
      el.hidden = true;
      try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
      fireDataLayer('tusq_inapp_banner_dismissed', { source: sourceTag() });
    });

    el.querySelector('[data-tusq-inapp-open]').addEventListener('click', function () {
      var url = window.location.href;
      var copied = copyToClipboard(url);
      fireDataLayer('tusq_inapp_banner_open_clicked', {
        source: sourceTag(),
        device: isIOS() ? 'ios' : isAndroid() ? 'android' : 'other',
        clipboard_copied: !!copied
      });

      if (isAndroid()) {
        var clean = url.replace(/^https?:\/\//, '');
        var intent = 'intent://' + clean + '#Intent;scheme=https;package=com.android.chrome;end';
        window.location.href = intent; return;
      }
      if (isIOS()) {
        var safariUrl = url.replace(/^https:/, 'x-safari-https:');
        window.location.href = safariUrl;
        setTimeout(function () {
          alert('If Safari didn’t open, the link is on your clipboard — open Safari and paste.');
        }, 900); return;
      }
      alert('Link copied — paste it in your default browser.');
    });

    _banner_wired = true;
  }

  // ── Decide when to show ───────────────────────────────────────────────────
  function maybeShow() {
    // (1) Direct fallback: if a user lands on /checkouts or /orders directly
    if (pathIsHighIntent()) { showBanner(); return; }
    // (2) Cart drawer already open on load (rare but possible)
    if (anyDrawerVisible()) { showBanner(); return; }
    // (3) PRIMARY trigger for Tusq: observe drawer open events
    startDrawerObserver();
    // (4) Broader net — listen for any custom event a drawer library may fire
    var events = ['cart:opened', 'cart-drawer:opened', 'tusq:cart:opened',
                  'zrx:cart:opened', 'CartDrawer:open', 'drawer:opened'];
    events.forEach(function (e) {
      document.addEventListener(e, function () { showBanner(); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeShow);
  } else {
    maybeShow();
  }
})();
